import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isNumber, sortBy } from 'es-toolkit/compat';
import { catchError, from, map, mergeMap, Observable, of, switchMap, toArray } from 'rxjs';

import { AggregatedStoryWithInsightDto } from '@malou-io/package-dto';
import { CsvAsStringArrays, getPlatformKeysWithStories, isNotNil, PlatformFilterPage, PlatformKey } from '@malou-io/package-utils';

import { StoriesApiService } from '#modules/posts/services/stories.api.service';
import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import {
    AbstractCsvService,
    DEFAULT_CSV_PAGINATION,
} from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { EnumTranslatePipe } from '#shared/pipes/enum-translate.pipe';

interface Data {
    restaurant: LightRestaurant;
    // Null when the stories of the restaurant failed to load.
    stories: AggregatedStoryWithInsightDto[] | null;
}

interface Options {
    restaurants: LightRestaurant[];
}

interface StoriesPeriod {
    platformKeys: PlatformKey[];
    startDate: string;
    endDate: string;
}

// A scheduled sync stores the stories of every active restaurant twice a day, so the stored ones miss at most those published since.
// Up to this count, the export still syncs each restaurant live, which keeps it to 20 Meta syncs for the freshest stories.
const LIVE_SYNC_MAX_RESTAURANT_COUNT = 20;
// Each sync calls Meta and downloads the new medias on the API, so one export runs at most five of them at once.
const LIVE_SYNC_CONCURRENT_RESTAURANT_COUNT = 5;

@Injectable()
export class AggregatedStoriesCsvInsightsService extends AbstractCsvService<Data[], Options> {
    private readonly _enumTranslatePipe = inject(EnumTranslatePipe);
    private readonly _translateService = inject(TranslateService);
    private readonly _storiesService = inject(StoriesApiService);
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected override _getData$({ restaurants }: Options): Observable<Data[]> {
        const { endDate, startDate } = this._aggregatedStatisticsStore.datesFilter();
        const platformKeys = getPlatformKeysWithStories().filter((platformKey) =>
            this._aggregatedStatisticsStore.getPlatformsFiltersByPage({ page: PlatformFilterPage.SOCIAL_NETWORKS }).includes(platformKey)
        );

        if (!startDate || !endDate || platformKeys.length === 0) {
            return of([]);
        }

        const period = { platformKeys, startDate: startDate.toISOString(), endDate: endDate.toISOString() };
        if (restaurants.length > LIVE_SYNC_MAX_RESTAURANT_COUNT) {
            return this._getStoredStories$(restaurants, period);
        }
        return this._getStoriesAfterLiveSync$(restaurants, period);
    }

    protected override _isDataValid(data: Data[]): boolean {
        return data.some((d) => d.stories === null || d.stories.length > 0);
    }

    protected override _getCsvHeaderRow(): CsvAsStringArrays[0] {
        return [
            'Date',
            'Hour',
            'Location',
            'Location Internal Name',
            'Location Address',
            'Platform',
            'Impressions',
            'Reach',
            'Exits',
            'Next story',
            'Previous story',
        ];
    }

    protected override _getCsvDataRows(data: Data[]): string[][] {
        return data
            .map(({ restaurant, stories }) => {
                if (stories === null) {
                    return [this._getLoadingFailureRow(restaurant)];
                }
                if (!stories.length) {
                    return null;
                }

                const locationCells = this._getLocationCells(restaurant);
                return stories.map((story) => {
                    const createdAt = new Date(story.createdAt);
                    const date = createdAt.toLocaleDateString();
                    const hour = `${createdAt.getHours()}:${createdAt.getMinutes()}`;
                    const platform = this._enumTranslatePipe.transform(story.key, 'platform_key');

                    return [
                        date,
                        hour,
                        ...locationCells,
                        platform,
                        this._formatMetric(story.impressions),
                        this._formatMetric(story.reach),
                        this._formatMetric(story.tapsExits),
                        this._formatMetric(story.tapsForward),
                        this._formatMetric(story.tapsBack),
                    ];
                });
            })
            .filter(isNotNil)
            .flat();
    }

    private _getStoriesAfterLiveSync$(restaurants: LightRestaurant[], period: StoriesPeriod): Observable<Data[]> {
        return from(restaurants).pipe(
            mergeMap(
                (restaurant, index) =>
                    this._storiesService.synchronizeStories$(restaurant.id).pipe(
                        // A failed sync leaves the stories the scheduled sync stored, so they are read anyway.
                        catchError(() => of(null)),
                        switchMap(() =>
                            this._storiesService.getStoriesWithInsights$(restaurant.id, {
                                ...period,
                                pagination: { ...DEFAULT_CSV_PAGINATION, skip: 0 },
                            })
                        ),
                        map((stories): Data => ({ restaurant, stories })),
                        catchError(() => of({ restaurant, stories: null })),
                        map((restaurantData) => ({ index, restaurantData }))
                    ),
                LIVE_SYNC_CONCURRENT_RESTAURANT_COUNT
            ),
            toArray(),
            map((results) => sortBy(results, 'index').map(({ restaurantData }) => restaurantData))
        );
    }

    private _getStoredStories$(restaurants: LightRestaurant[], period: StoriesPeriod): Observable<Data[]> {
        return this._storiesService
            .getAggregatedStoriesWithInsights$({ restaurantIds: restaurants.map((restaurant) => restaurant.id), ...period })
            .pipe(
                map((restaurantsStories) => {
                    const storiesByRestaurantId = new Map(restaurantsStories.map(({ restaurantId, stories }) => [restaurantId, stories]));
                    // A restaurant missing from the answer is one whose social statistics the user cannot read.
                    return restaurants.map((restaurant) => ({ restaurant, stories: storiesByRestaurantId.get(restaurant.id) ?? [] }));
                }),
                catchError(() => of(restaurants.map((restaurant) => ({ restaurant, stories: null }))))
            );
    }

    private _getLoadingFailureRow(restaurant: LightRestaurant): string[] {
        const loadingFailure = this._translateService.instant('aggregated_statistics.download_insights_modal.stories_loading_failed');
        return ['', '', ...this._getLocationCells(restaurant), loadingFailure, '', '', '', '', ''];
    }

    private _getLocationCells(restaurant: LightRestaurant): string[] {
        return [restaurant.name, restaurant.internalName ?? '', restaurant.getFullFormattedAddress().replace(/,/g, '')];
    }

    private _formatMetric(value: number | null): string {
        if (!isNumber(value)) {
            return 'N/A';
        }
        return value < 0 ? '< 5' : value.toString();
    }
}
